import _omit from 'lodash/omit'
import {
  Block,
  ListView,
  WorkflowBlock,
  isBlockWithChildren,
} from '@basehub/data/replicache/index'
import {
  isEmptyJson,
  normalizeJsonState,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { emptyTipTapContentHash } from '@basehub/tiptap-api-utils/hash'
import { idToSourceBlockId } from '../../mutators/repo-space/block/mutators/copy-on-write'
import { SetOptional } from 'type-fest'

/**
 * Fast deep equality check for JSON-like values (objects, arrays, primitives).
 * Doesn't handle Map, Set, Date, or other special objects - only JSON-serializable data.
 */
function deepEqual(
  a: any,
  b: any,
  path: string = '',
  debug?: boolean
): boolean {
  // Primitive equality
  if (a === b) return true

  // Handle null/undefined
  if (a == null || b == null) {
    const result = a === b
    if (debug && !result) {
      console.log(`[deepEqual] null/undefined mismatch at ${path}:`, { a, b })
    }
    return result
  }

  // Type check
  if (typeof a !== typeof b) {
    if (debug) {
      console.log(`[deepEqual] type mismatch at ${path}:`, {
        typeA: typeof a,
        typeB: typeof b,
        a,
        b,
      })
    }
    return false
  }

  // Array comparison
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) {
      if (debug) console.log(`[deepEqual] array/non-array mismatch at ${path}`)
      return false
    }
    if (a.length !== b.length) {
      if (debug) {
        console.log(`[deepEqual] array length mismatch at ${path}:`, {
          lengthA: a.length,
          lengthB: b.length,
        })
      }
      return false
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i], `${path}[${i}]`, debug)) {
        if (debug) {
          console.log(`[deepEqual] array element differs at ${path}[${i}]`)
        }
        return false
      }
    }
    return true
  }

  // Object comparison
  if (typeof a === 'object') {
    // Filter out undefined values - treat {a: 1, b: undefined} same as {a: 1}
    const keysA = Object.keys(a).filter((k) => a[k] !== undefined)
    const keysB = Object.keys(b).filter((k) => b[k] !== undefined)

    if (keysA.length !== keysB.length) {
      if (debug) {
        console.log(`[deepEqual] object key count mismatch at ${path}:`, {
          keysA: keysA.length,
          keysB: keysB.length,
          keysOnlyInA: keysA.filter((k) => !keysB.includes(k)),
          keysOnlyInB: keysB.filter((k) => !keysA.includes(k)),
          objectA: a,
          objectB: b,
          keysListA: keysA,
          keysListB: keysB,
        })
      }
      return false
    }

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) {
        if (debug) {
          console.log(`[deepEqual] key missing in b at ${path}:`, { key })
        }
        return false
      }
      if (!deepEqual(a[key], b[key], `${path}.${key}`, debug)) {
        if (debug) {
          console.log(`[deepEqual] value differs at ${path}.${key}`)
        }
        return false
      }
    }

    return true
  }

  // Primitive values already handled by === at the top
  if (debug) {
    console.log(`[deepEqual] primitive mismatch at ${path}:`, { a, b })
  }
  return false
}

/**
 * Extracts comparison data from a block.
 * This is the same logic as the old hashBlock() but returns the data object instead of hashing it.
 */
export function getBlockComparisonData(
  block: Block,
  opts?: {
    omitKeys?: string[]
    preBuiltTipTapJson?: unknown
    debug?: boolean
    trimUndefined?: boolean
  }
): Record<string, unknown> {
  let comparisonData: Record<string, unknown> = {}

  if (block.instanceChildTarget) {
    /**
     * Blocks below instances inherit all properties from the main Component block,
     * but the value can be overriden. So we compare just the value, as that's what the user cares about.
     */
    switch (block.type) {
      case 'root':
        throw new Error(
          `Root blocks inside an instance? Whoa, that's impressive.`
        )
      case 'document':
      case 'component':
      case 'instance':
      case 'list':
      case 'reference': {
        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          value: {
            ...('targetBlockIds' in block.value &&
            block.value.targetBlockIds?.length
              ? { targetBlockIds: block.value.targetBlockIds }
              : {}),
          },
          ...('variantOverrides' in block && block.variantOverrides
            ? { variantOverrides: block.variantOverrides }
            : {}),
          instanceChildTarget: block.instanceChildTarget || undefined,
        }
        if (block.type !== 'list') {
          comparisonData.slug = block.slug || undefined
        }
        break
      }
      case 'comment': {
        // comments are not part of instances
        break
      }
      case 'rich-text': {
        const json = opts?.preBuiltTipTapJson
          ? normalizeJsonState(opts.preBuiltTipTapJson)
          : stateToJson(block.value.state)

        const overrides = Object.values(block.variantOverrides ?? {})
          .sort()
          .map((i) => {
            return i.hash
          })

        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          value: isEmptyJson(json) ? emptyTipTapContentHash() : json,
          ...(overrides.length > 0 ? { variantOverrides: overrides } : {}),
          instanceChildTarget: block.instanceChildTarget || undefined,
        }
        break
      }
      case 'og-image': {
        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          value: block.value,
          ...('variantOverrides' in block && block.variantOverrides
            ? { variantOverrides: block.variantOverrides }
            : {}),
          instanceChildTarget: block.instanceChildTarget || undefined,
        }
        break
      }
      case 'workflow': {
        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          instanceChildTarget: block.instanceChildTarget || undefined,
        }
        break
      }
      default: {
        if (isBlockWithChildren(block)) {
          throw new Error(
            `Blocks with children should be handled individually. Processing ${block}`
          )
        }

        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          value: block.value,
          ...('variantOverrides' in block && block.variantOverrides
            ? { variantOverrides: block.variantOverrides }
            : {}),
          instanceChildTarget: block.instanceChildTarget || undefined,
        }
        break
      }
    }
  } else {
    switch (block.type) {
      case 'root':
        comparisonData = {
          /**
           * For the root, we include its id and updatedAt because:
           * 1) id, to make sure it does not match with another root block. We don't want root block reutilization.
           * 2) updatedAt, for the frontend to update when a block inside the tree changes. block updates will update the root's updatedAt.
           * 3) no other thing, cause they don't change / are useless.
           */
          id: block.id,
          updatedAt: block.updatedAt,
        }
        break
      case 'document':
      case 'component':
      case 'instance':
      case 'reference':
        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          type: block.type,
          title: block.title,
          value: {
            ...('targetBlockIds' in block.value &&
            block.value.targetBlockIds?.length
              ? { targetBlockIds: block.value.targetBlockIds }
              : {}),
          },
          constraints: block.constraints,
          typeOptions: block.typeOptions,
          hidden: block.hidden,
          searchConfig: block.searchConfig
            ? {
                ...(block.searchConfig.index ? { index: true } : {}),
                ...('generateEmbeddings' in block.searchConfig &&
                block.searchConfig.generateEmbeddings
                  ? {
                      generateEmbeddings: block.searchConfig.generateEmbeddings,
                    }
                  : {}),
              }
            : undefined,
          ...('previewUrl' in block && block.previewUrl
            ? { previewUrl: block.previewUrl || undefined }
            : {}),
          ...(block.description
            ? { description: block.description || undefined }
            : {}),
          ...('variantOverrides' in block && block.variantOverrides
            ? { variantOverrides: block.variantOverrides }
            : {}),
          apiName: block.apiName || undefined,
          slug: block.slug || undefined,
        }
        break
      case 'list':
        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          type: block.type,
          title: block.title,
          value: {
            ...(block.value.views?.length
              ? {
                  views: block.value.views.map((v) => {
                    const view: SetOptional<
                      ListView,
                      'columnsMetadata' | 'showOnFirstLoad'
                    > = structuredClone(v)
                    delete view.columnsMetadata
                    delete view.showOnFirstLoad
                    return view
                  }),
                }
              : {}),
          },
          constraints: block.constraints,
          typeOptions: block.typeOptions,
          hidden: block.hidden,
          searchConfig: block.searchConfig?.index ? { index: true } : undefined,
          ...('previewUrl' in block && block.previewUrl
            ? { previewUrl: block.previewUrl }
            : {}),
          ...(block.description
            ? { description: block.description || undefined }
            : {}),
          apiName: block.apiName || undefined,
        }
        break
      case 'comment': {
        const json = opts?.preBuiltTipTapJson ?? stateToJson(block.value.state)

        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          type: block.type,
          value: isEmptyJson(json) ? emptyTipTapContentHash() : json,
          constraints: block.constraints,
          hidden: block.hidden,
        }
        break
      }
      case 'agent': {
        const json = opts?.preBuiltTipTapJson ?? stateToJson(block.value.state)

        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          title: block.title,
          description: block.description,
          type: block.type,
          value: isEmptyJson(json) ? emptyTipTapContentHash() : json,
          constraints: block.constraints,
          typeOptions: block.typeOptions,
        }
        break
      }
      case 'workflow': {
        const workflowBlock: Omit<WorkflowBlock, 'value'> & {
          value: SetOptional<WorkflowBlock['value'], 'activeRuns'>
        } = structuredClone(block)

        if ('activeRuns' in workflowBlock.value) {
          delete workflowBlock.value.activeRuns
        }

        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          value: workflowBlock.value,
          type: block.type,
          title: block.title,
          constraints: block.constraints,
          typeOptions: block.typeOptions,
          hidden: block.hidden,
          apiName: block.apiName || undefined,
          searchConfig: block.searchConfig?.index ? { index: true } : undefined,
          ...(block.description
            ? { description: block.description || undefined }
            : {}),
          ...('variantOverrides' in block && block.variantOverrides
            ? { variantOverrides: block.variantOverrides }
            : {}),
        }
        break
      }
      case 'rich-text': {
        const json = opts?.preBuiltTipTapJson
          ? normalizeJsonState(opts.preBuiltTipTapJson)
          : stateToJson(block.value.state)

        const overrides = Object.values(block.variantOverrides ?? {})
          .sort()
          .map((i) => {
            return i.hash
          })

        const constraintsToHash = structuredClone(block.constraints)
        if (block.constraints.layout === 'multi-line') {
          // we treat multi-line as the default state so we don't add it to the comparison result
          delete constraintsToHash.layout
        }

        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          value: isEmptyJson(json) ? emptyTipTapContentHash() : json,
          type: block.type,
          title: block.title,
          constraints: constraintsToHash,
          typeOptions: block.typeOptions,
          hidden: block.hidden,
          apiName: block.apiName || undefined,
          searchConfig: block.searchConfig?.index ? { index: true } : undefined,
          ...(block.description
            ? { description: block.description || undefined }
            : {}),
          ...(overrides.length > 0 ? { variantOverrides: overrides } : {}),
        }
        break
      }
      default:
        if (isBlockWithChildren(block)) {
          throw new Error(
            `Blocks with children should be handled individually. Processing ${block}`
          )
        }
        comparisonData = {
          sourceBlockId: idToSourceBlockId(block),
          value: block.value,
          type: block.type,
          title: block.title,
          constraints: block.constraints,
          typeOptions: block.typeOptions,
          hidden: block.hidden,
          apiName: block.apiName || undefined,
          searchConfig: block.searchConfig?.index ? { index: true } : undefined,
          ...(block.description
            ? { description: block.description || undefined }
            : {}),
          ...('variantOverrides' in block && block.variantOverrides
            ? { variantOverrides: block.variantOverrides }
            : {}),
        }
        break
    }
  }

  if (opts?.omitKeys) {
    comparisonData = _omit(comparisonData, opts.omitKeys)
  }

  return comparisonData
}

/**
 * Checks if two blocks are compatible (have the same semantic content).
 * This replaces hash-based comparison.
 * Uses a custom deep equality function optimized for JSON-like data.
 */
export function areBlocksCompatible(
  block1: Block,
  block2: Block,
  opts?: {
    preBuiltTipTapJson1?: unknown
    preBuiltTipTapJson2?: unknown
    omitKeys?: string[]
    debug?: boolean
  }
): boolean {
  const data1 = getBlockComparisonData(block1, {
    preBuiltTipTapJson: opts?.preBuiltTipTapJson1,
    omitKeys: opts?.omitKeys,
  })
  const data2 = getBlockComparisonData(block2, {
    preBuiltTipTapJson: opts?.preBuiltTipTapJson2,
    omitKeys: opts?.omitKeys,
  })

  const result = deepEqual(data1, data2, 'root', opts?.debug)

  if (opts?.debug) {
    console.log('[areBlocksCompatible] Final result:', {
      result,
      data1Keys: Object.keys(data1),
      data2Keys: Object.keys(data2),
    })
  }

  return result
}
